import cloneDeep from 'lodash/cloneDeep'
import { SectionType } from './section'
import { ServiceCustomerType } from '@/_api/models/service'
import { ReservationBufferTimeMode } from '@/_api/models/reservationServiceModel'
import { useNuxtApp } from '#app'
import { useUIStore } from '~/stores/useUIStore'

export class BufferTimes {
  constructor (preBuffer, postBuffer) {
    this.preBuffer = preBuffer
    this.postBuffer = postBuffer
  }
}

export class Block {
  constructor () {
    this.hash = this.hash()
    this.level = null
    this.forwardBlocks = []
    this.backwardCoord = null
    this.forwardCoord = null
    this.forwardPressure = null
    this.relatedBlocks = null
    this.focused = null
    this.highlight = null
    this.initialPosition = null
  }

  get shouldOverlap () {
    return false
  }

  get duration () {
    return this.finishTime - this.startTime
  }

  get startTime () {
    return 0
  }

  get finishTime () {
    return 0
  }

  get defaultZIndex () {
    return 0
  }

  get zIndex () {
    let zIndex = this.defaultZIndex

    if (this.shouldOverlap) {
      zIndex += this.level
    }

    return zIndex
  }

  get isTimingDuration () {
    return this.timing === Timing.duration
  }

  get isTimingFinishing () {
    return this.timing === Timing.finishing
  }

  hash () {
    let dt = new Date().getTime()
    const guid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (dt + Math.random() * 16) % 16 | 0
      dt = Math.floor(dt / 16)
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
    })
    return guid
  }

  rectForWidth (width) {
    // double the width, but don't go beyond the maximum forward coordinate (1.0)
    // const forwardCoord = Math.min(1.0, (this.backwardCoord + ((this.forwardCoord || 0) - this.backwardCoord)) * 1.5)

    const forwardCoord = this.forwardCoord

    const left = this.backwardCoord
    const right = (1 - forwardCoord)

    const overlap = 0
    const x = Math.max(0, (width * left) - overlap)
    let newWidth = width - (width * right) - (width * left)
    if (left > 0) {
      newWidth += overlap
    }
    if (right > 0) {
      newWidth += overlap
    }

    newWidth = Math.min((width - x), newWidth)

    return {
      x,
      y: 0,
      width: newWidth,
      height: 0,
    }
  }

  collides (otherBlock) {
    return (this.shouldOverlap && otherBlock.shouldOverlap) && this.finishTime > otherBlock.startTime && this.startTime < otherBlock.finishTime
  }

  resetPressure () {
    this.level = null
    this.forwardBlocks = []
    this.backwardCoord = null
    this.forwardCoord = null
    this.forwardPressure = null
  }

  setInitialPosition () {
    this.initialPosition = {
      startTime: this.startTime,
      duration: this.duration,
      section: this.section,
    }
  }

  resetInitialPosition () {
    this.startTime = this.initialPosition.startTime
    this.duration = this.initialPosition.duration
    this.section = this.initialPosition.section
  }
}

export const Timing = Object.freeze({
  duration: 1,
  finishing: 2,
})

export class ReservationBlock extends Block {
  constructor (reservation, reservationService, { timing = Timing.duration, type = SectionType.employee } = {}) {
    super()

    this.reservation = reservation
    this.reservationService = reservationService
    this.timing = timing
    this.type = type

    this.durationBlock = null
    this.finishingBlock = null
  }

  get shouldOverlap () {
    return true
  }

  get startTime () {
    const startTime = this.reservationService.startTime

    if (this.timing === Timing.duration) {
      return startTime
    } else if (this.timing === Timing.finishing) {
      const duration = this.reservationService.duration
      const processing = this.reservationService.processing

      return startTime + duration + processing
    }

    return undefined
  }

  set startTime (val) {
    if (this.timing === Timing.duration) {
      this.reservationService.startTime = val
    } else if (this.timing === Timing.finishing) {
      const duration = this.reservationService.duration
      const startTime = this.reservationService.startTime

      const processing = val - duration - startTime

      if (processing >= 0) {
        this.reservationService.processing = processing
      }
    }
  }

  get finishTime () {
    const startTime = this.reservationService.startTime
    const duration = this.reservationService.duration

    if (this.timing === Timing.duration) {
      return startTime + duration
    } else if (this.timing === Timing.finishing) {
      const processing = this.reservationService.processing
      const finishing = this.reservationService.finishing
      return startTime + duration + processing + finishing
    }

    return undefined
  }

  get duration () {
    if (this.timing === Timing.duration) {
      return this.reservationService.duration
    } else if (this.timing === Timing.finishing) {
      return this.reservationService.finishing
    }

    return undefined
  }

  set duration (val) {
    if (this.timing === Timing.duration) {
      this.reservationService.duration = val
    } else if (this.timing === Timing.finishing) {
      this.reservationService.finishing =  val
    }
  }

  get section () {
    if (this.type === SectionType.employee) {
      const date = this.reservation.date.value
      const employee = this.reservationService.idEmployee

      return `${date}-${employee}`
    }

    if (this.type === SectionType.date) {
      const date = this.reservation.date.value

      return date
    }

    return undefined
  }

  set section (val) {
    if (val) {
      const [date, employee] = val.split('-')

      this.reservation.date.value = date

      if (this.type === SectionType.employee && employee) {
        this.reservationService.idEmployee =  employee
      }
    }
  }

  get defaultZIndex () {
    return 10
  }

  get label () {
    const privacyMode = useUIStore().privacyMode
    const type = this.reservation.customerType
    const serviceTitle = this.reservationService.service?.title || ''

    if (privacyMode) {
      return serviceTitle
    }

    if (type === ServiceCustomerType.group) {
      const { $i18n } = useNuxtApp()
      return $i18n.t('components.schedule.provider.block.label', { title: serviceTitle, count: this.reservation.attendees.count }, this.reservation.attendees.count)
    }

    if (type === ServiceCustomerType.single && this.reservation.customer) {
      return `${this.reservation.customer.displayName} - ${serviceTitle}`
    }

    return ''
  }

  get colour () {
    const service = this.reservationService.service
    return {
      border: service ? '#' + service.borderHexColour : '#888888',
      text: service ? '#' + service.borderHexColour : '#888888',
      background: service ? '#' + service.hexColour : '#eeeeee',
    }
  }

  get class () {
    return `reservation-${this.reservation.idReservation}`
  }

  get isReservationBlock () {
    return true
  }

  updateSection (section) {
    if (this.type === SectionType.employee) {
      this.reservationService.employee = section.employee
    }
    // this.reservation.date = section.date
  }

  updateReservation (reservation) {
    this.reservation = reservation

    for (const reservationService of reservation.services) {
      if (this.reservationService.idReservationService === reservationService.idReservationService) {
        this.reservationService = reservationService
      }
    }
  }

  reservationFinishingBlock () {
    if (this.reservationService.finishing === 0) {
      return null
    }

    const finishingBlock = new ReservationBlock(this.reservation, this.reservationService, { timing: Timing.finishing })
    this.linkFinishingBlock(finishingBlock)
    return finishingBlock
  }

  linkFinishingBlock (finishingBlock) {
    this.finishingBlock = finishingBlock
    finishingBlock.durationBlock = this
  }

  applyInterserviceBufferTimes () {
    if (this.durationBlock) { return }

    if (this.reservationService.bufferTimeMode === ReservationBufferTimeMode.interService) {
      this.applyBufferTimes(new BufferTimes(this.reservationService.bufferTimePre || 0, this.reservationService.bufferTimePost || 0))
    } else {
      this.applyBufferTimes(new BufferTimes(0, 0))
    }
  }

  applyBufferTimes (bufferTimes) {
    if (this.finishingBlock instanceof ReservationBlock) {
      this.finishingBlock.bufferTimes = new BufferTimes(0, bufferTimes.postBuffer)
      this.bufferTimes = new BufferTimes(bufferTimes.preBuffer, 0)
    } else {
      this.bufferTimes = bufferTimes
    }
  }
}

export class ReservationResourceBlock extends ReservationBlock {
  constructor (reservation, reservationService, reservationResource, { timing = Timing.duration, type = SectionType.employee } = {}) {
    super(reservation, reservationService, { timing, type })

    this.reservationResource = reservationResource
  }

  get resourceLabel () {
    const resourceName = this.reservationResource?.serviceResource?.name || ''
    const displayName = this.reservationResource?.resource?.displayName || ''
    return `${resourceName}: ${displayName}`
  }

  get section () {
    if (this.type === SectionType.employee) {
      const date = this.reservation.date.value
      const employee = Number(this.reservationResource.idEmployee)

      return `${date}-${employee}`
    }

    if (this.type === SectionType.date) {
      const date = this.reservation.date.value

      return date
    }

    return undefined
  }

  set section (val) {
    if (val) {
      const [date, employee] = val.split('-')

      this.reservation.date.value = date

      if (this.type === SectionType.employee && employee) {
        this.reservationResource.idEmployee =  employee
      }
    }
  }

  get isResourceBlock () {
    return true
  }

  reservationFinishingBlock () {
    if (this.reservationService.finishing === 0) {
      return null
    }

    const finishingBlock = new ReservationResourceBlock(this.reservation, this.reservationService, this.reservationResource, { timing: Timing.finishing })
    this.linkFinishingBlock(finishingBlock)
    return finishingBlock
  }
}

export class ReservationCustomerResourceBlock extends ReservationBlock {
  constructor (reservation, reservationService, reservationCustomerResource, { timing = Timing.duration, type = SectionType.employee } = {}) {
    super(reservation, reservationService, { timing, type })

    this.reservationResource = reservationCustomerResource
  }

  get label () {
    const type = this.reservation.customerType
    const serviceTitle = this.reservationService.service?.title || ''

    if (type === ServiceCustomerType.group) {
      return `${serviceTitle} - ${this.reservationResource.customer.displayName}`
    }

    if (type === ServiceCustomerType.single) {
      return `${this.reservation.customer.displayName} - ${serviceTitle}`
    }

    return undefined
  }

  get resourceLabel () {
    const resourceName = this.reservationResource?.serviceResource?.name || ''
    const customerName = this.reservationResource?.customer?.displayName || ''
    const displayName = this.reservationResource?.resource?.displayName || ''
    return `${resourceName} (${customerName}): ${displayName}`
  }

  get section () {
    if (this.type === SectionType.employee) {
      const date = this.reservation.date.value
      const employee = Number(this.reservationResource.idEmployee)

      return `${date}-${employee}`
    }

    if (this.type === SectionType.date) {
      const date = this.reservation.date.value

      return date
    }

    return undefined
  }

  set section (val) {
    if (val) {
      const [date, employee] = val.split('-')

      this.reservation.date.value = date

      if (this.type === SectionType.employee && employee) {
        this.reservationResource.idEmployee =  employee
      }
    }
  }

  get isResourceBlock () {
    return true
  }

  reservationFinishingBlock () {
    if (this.reservationService.finishing === 0) {
      return null
    }

    const finishingBlock = new ReservationResourceBlock(this.reservation, this.reservationService, this.reservationResource, { timing: Timing.finishing })
    this.linkFinishingBlock(finishingBlock)
    return finishingBlock
  }
}

export class BreakBlock extends Block {
  constructor (employeeBreak, { type = SectionType.employee } = {}) {
    super()

    this.employeeBreak = employeeBreak
    this.type = type
  }

  get startTime () {
    return this.employeeBreak.time
  }

  set startTime (val) {
    this.employeeBreak.time = val
  }

  get finishTime () {
    return this.employeeBreak.time + this.employeeBreak.duration
  }

  get duration () {
    return this.employeeBreak.duration
  }

  set duration (val) {
    this.employeeBreak.duration =  val
  }

  get section () {
    if (this.type === SectionType.employee) {
      const date = this.employeeBreak.date.value
      const employee = Number(this.employeeBreak.employee.idEmployee)

      return `${date}-${employee}`
    }

    if (this.type === SectionType.date) {
      const date = this.employeeBreak.date.value

      return date
    }

    return undefined
  }

  set section (val) {
    if (val) {
      const [date, employee] = val.split('-')

      this.employeeBreak.date.value = date

      if (this.type === SectionType.employee && employee) {
        this.employeeBreak.employee.idEmployee = employee
      }
    }
  }

  get defaultZIndex () {
    return 1
  }

  get class () {
    return `break-${this.employeeBreak.id}`
  }

  get isBreakBlock () {
    return true
  }

  updateSection (section) {
    if (this.type === SectionType.employee) {
      this.employeeBreak.employee = cloneDeep(section.employee)
    }
    // this.employeeBreak.employee = cloneDeep(section.employee)
    // this.employee = cloneDeep(section.employee)
    // this.employeeBreak.date = section.date
  }
}
